/**
 * DESIGN TOKENS — source de vérité du design system Syndays/Apteed.
 *
 * Toute la refonte visuelle part d'ici : les surcharges de composants
 * (src/theme/index.js) ne font que consommer ces jetons, jamais de
 * valeurs magiques dispersées dans les pages.
 */

// Marque : bleu nuit profond (historique #1b2445) + accent bleu électrique.
export const brand = {
  900: '#0d1330',
  800: '#141b3a',
  700: '#1b2445', // couleur historique de la marque
  600: '#2a3663',
  500: '#3b4a82',
  400: '#4A86CE',
  300: '#7fa9dd',
  200: '#b9d0ee',
  100: '#e3ecf8'
};

export const accent = {
  900: '#1e3a8a',
  800: '#1d4ed8',
  700: '#2563eb',
  600: '#3b82f6',
  500: '#60a5fa',
  400: '#93c5fd',
  300: '#bfdbfe',
  200: '#dbeafe',
  100: '#eff6ff'
};

export const neutral = {
  950: '#020617',
  900: '#0f172a',
  800: '#1e293b',
  700: '#334155',
  600: '#475569',
  500: '#64748b',
  400: '#94a3b8',
  300: '#cbd5e1',
  200: '#e2e8f0',
  100: '#f1f5f9',
  50: '#f8fafc',
  0: '#ffffff'
};

export const semantic = {
  success: { main: '#12b76a', light: '#32d583', dark: '#027a48', soft: '#ecfdf3' },
  warning: { main: '#f79009', light: '#fdb022', dark: '#b54708', soft: '#fffaeb' },
  error: { main: '#f04438', light: '#fda29b', dark: '#b42318', soft: '#fef3f2' },
  info: { main: '#0ba5ec', light: '#36bffa', dark: '#026aa2', soft: '#f0f9ff' }
};

export const gradients = {
  brand: 'linear-gradient(135deg, #1b2445 0%, #2a3663 55%, #3b82f6 165%)',
  accent: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
  // « Aurora » : la signature visuelle du produit. On la retrouve sur le
  // liseré de la barre haute, l'onglet actif, l'item de menu actif, le
  // bouton primaire et le fond de la page de connexion.
  aurora: 'linear-gradient(90deg, #3b82f6 0%, #6366f1 38%, #8b5cf6 68%, #38bdf8 100%)',
  auroraSoft: 'linear-gradient(135deg, rgba(59, 130, 246, 0.16) 0%, rgba(139, 92, 246, 0.14) 55%, rgba(56, 189, 248, 0.16) 100%)',
  primaryButton: 'linear-gradient(135deg, #2a3663 0%, #1b2445 55%, #172046 100%)',
  primaryButtonHover: 'linear-gradient(135deg, #34427a 0%, #232e58 55%, #1d2650 100%)',
  success: 'linear-gradient(135deg, #12b76a 0%, #32d583 100%)',
  warning: 'linear-gradient(135deg, #f79009 0%, #fdb022 100%)',
  danger: 'linear-gradient(135deg, #f04438 0%, #fda29b 100%)',
  surfaceLight: 'linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%)',
  surfaceDark: 'linear-gradient(180deg, #18212f 0%, #141b27 100%)',
  // Rail de navigation : le panneau sombre qui ancre l'identité. Deux
  // variantes (thème clair / sombre), leurs halos et le fondu du bas.
  rail: 'linear-gradient(175deg, #232e5c 0%, #18214a 42%, #0f1633 100%)',
  railDark: 'linear-gradient(175deg, #1a2434 0%, #131b28 42%, #0b111c 100%)',
  railHalo: 'radial-gradient(circle, rgba(99, 102, 241, 0.35) 0%, rgba(99, 102, 241, 0) 70%)',
  railHaloDark: 'radial-gradient(circle, rgba(237, 179, 130, 0.18) 0%, rgba(237, 179, 130, 0) 70%)',
  railFade: 'linear-gradient(180deg, rgba(15, 22, 51, 0) 0%, rgba(15, 22, 51, 0.85) 100%)',
  railFadeDark: 'linear-gradient(180deg, rgba(11, 17, 28, 0) 0%, rgba(11, 17, 28, 0.9) 100%)'
};

export const FONT_SANS = '"Inter", "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"';
export const FONT_DISPLAY = '"Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

export const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
export const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';
export const RADIUS = 12;

/** Rayons par famille de composants (contrôle / carte / surface flottante). */
export const RADII = {
  control: 10,
  card: 14,
  overlay: 20,
  pill: 999
};

/**
 * Réglages d'animation partagés (framer-motion).
 * Un ressort unique pour toutes les entrées : l'app doit avoir une seule
 * « physique », sinon chaque écran semble venir d'un produit différent.
 */
export const MOTION = {
  spring: { type: 'spring', stiffness: 260, damping: 24, mass: 0.9 },
  springSoft: { type: 'spring', stiffness: 180, damping: 22 },
  fast: { duration: 0.18, ease: [0.16, 1, 0.3, 1] },
  container: {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.04 } }
  },
  item: {
    hidden: { opacity: 0, y: 12 },
    show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 24 } }
  }
};

/** Jetons dérivés, par mode clair/sombre. */
export const surfaceTokens = (isLight) => ({
  surface: isLight ? '#ffffff' : '#161e2d',
  canvas: isLight ? '#f5f7fb' : '#0d1420',
  line: isLight ? '#e8ecf3' : 'rgba(148, 163, 184, 0.16)',
  lineStrong: isLight ? '#dbe2ec' : 'rgba(148, 163, 184, 0.28)',
  textPrimary: isLight ? '#111a2e' : '#e8edf6',
  textSecondary: isLight ? '#64748b' : '#94a3b8',
  hover: isLight ? 'rgba(27, 36, 69, 0.04)' : 'rgba(255, 255, 255, 0.05)',
  // Opaque des deux côtés : les en-têtes de tableaux sont collants,
  // un fond translucide laisserait défiler le contenu derrière eux.
  headTint: isLight ? '#f7f9fc' : '#1a2333',
  focusRing: isLight ? 'rgba(59, 130, 246, 0.18)' : 'rgba(96, 165, 250, 0.24)',
  focusColor: isLight ? accent[600] : accent[500],
  overlayShadow: isLight
    ? '0 0 0 1px rgba(15, 23, 42, 0.05), 0 12px 32px -8px rgba(15, 23, 42, 0.16), 0 4px 8px -4px rgba(15, 23, 42, 0.08)'
    : '0 0 0 1px rgba(148, 163, 184, 0.1), 0 16px 40px -8px rgba(0, 0, 0, 0.6)'
});

const designTokens = {
  brand,
  accent,
  neutral,
  semantic,
  gradients,
  FONT_SANS,
  FONT_DISPLAY,
  EASE,
  EASE_OUT,
  RADIUS,
  RADII,
  MOTION
};

export default designTokens;
